/**
 * Design → instance activity crosswalk (S3b).
 *
 * The study-design layer (Apollo `study_design_soa.form_name`) and the Visits & Activities
 * instance layer (`individual_activity_completion.activity_name`) describe the same clinical
 * activities with different naming conventions — design uses packed codes, instance uses display
 * names:  HAQDI↔HAQ-DI, EQ5D5LT↔EQ-5D-5L, SF36v2AT↔SF-36 Acute. There is no shared key in the data
 * (verified: 3/51 exact overlap), so this crosswalk bridges them for the Study-Design → V&A
 * drill-through (S4).
 *
 * Coverage is validated for CYTB323K12201: all 12 clinical design forms resolve to an instance
 * activity (7 via normalization, 5 via the curated alias table below). Non-clinical / eCOA system
 * forms (Activation, EstablishID, …) and trials without an Apollo clinical schedule (UPB-CP-03)
 * deliberately do not resolve — callers must render NO drill-through link in that case rather than
 * a dead one.
 */

/** Normalize a design form or instance activity name to a comparable key. */
function normalizeActivity(name: string): string {
  return name
    .toLowerCase()
    .replace(/v\d+/g, "") // drop version tokens (v2)
    .replace(/[^a-z0-9]/g, "") // drop separators/spaces/case
    .replace(/\d+$/, ""); // drop trailing version digits (2.0)
}

/**
 * Curated aliases for design forms whose packed code can't be normalized onto the instance
 * display name (version suffixes, sub-scale renames). Keyed by the raw design form_name.
 */
const DESIGN_TO_INSTANCE_ALIAS: Record<string, string> = {
  EQ5D5LT: "EQ-5D-5L",
  FACITFT: "FACIT-Fatigue",
  SF36v2AT: "SF-36 Acute",
  SHAQDI: "SHAQ",
  UlcerCount: "Digital Ulcer Count",
};

/**
 * Resolve a design form_name to the matching instance activity_name, or null when there is no
 * clinical counterpart (system/eCOA form, or a trial with no Apollo schedule). Pass the list of
 * instance activity names actually present for the trial so matches are trial-scoped and real.
 */
export function designFormToInstanceActivity(
  formName: string,
  instanceActivities: string[],
): string | null {
  const alias = DESIGN_TO_INSTANCE_ALIAS[formName];
  if (alias) {
    return instanceActivities.includes(alias) ? alias : null;
  }
  const key = normalizeActivity(formName);
  const hit = instanceActivities.find((a) => normalizeActivity(a) === key);
  return hit ?? null;
}
